// boardTypes.ts — Sprint 15. Tab-scope types + tag/status palette
// + the two number generators used by the Add-Meeting and
// Add-Resolution modals. Pure module — no React, no antd.
import dayjs from 'dayjs';

export type MeetingType   = 'agm' | 'board' | 'special' | 'committee';
export type MeetingStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
export type VoteStatus    = 'open' | 'closed';

export const MEETING_TYPE_CONFIG: Record<MeetingType, { label: string; color: string }> = {
  agm:       { label: 'Annual General Meeting', color: 'blue'   },
  board:     { label: 'Board Meeting',          color: 'purple' },
  special:   { label: 'Special Meeting',        color: 'orange' },
  committee: { label: 'Committee Meeting',      color: 'cyan'   },
};

export const MEETING_STATUS_CONFIG: Record<MeetingStatus, { color: string; label: string }> = {
  scheduled:   { color: 'blue',    label: 'Scheduled'  },
  in_progress: { color: 'warning', label: 'In Progress' },
  completed:   { color: 'success', label: 'Completed'   },
  cancelled:   { color: 'error',   label: 'Cancelled'   },
};

// ── Enum-case normalization (Sprint 122 / R9-MED-4) ────────────────────────
// The backend persists meeting `status`/`type` as UPPERCASE enums
// (`SCHEDULED`, `COMPLETED`, `BOARD`, …) while these config maps are keyed
// lowercase. Look up case-insensitively so an UPPERCASE value from the API
// still resolves to its chip + counts toward "Upcoming Meetings". Mirrors how
// resolutions/bookings are lower-cased at the store boundary.

/** Normalize any enum-ish value to the lowercase key used by the configs. */
export const normalizeMeetingStatus = (v?: string | null): MeetingStatus | undefined =>
  v == null ? undefined : (String(v).toLowerCase() as MeetingStatus);

export const normalizeMeetingType = (v?: string | null): MeetingType | undefined =>
  v == null ? undefined : (String(v).toLowerCase() as MeetingType);

/** Case-insensitive config lookups (return undefined for unknown keys). */
export const meetingStatusConfig = (v?: string | null) => {
  const k = normalizeMeetingStatus(v);
  return k ? MEETING_STATUS_CONFIG[k] : undefined;
};

export const meetingTypeConfig = (v?: string | null) => {
  const k = normalizeMeetingType(v);
  return k ? MEETING_TYPE_CONFIG[k] : undefined;
};

/** A meeting counts as "scheduled" regardless of backend casing. */
export const isScheduledStatus = (v?: string | null): boolean =>
  normalizeMeetingStatus(v) === 'scheduled';

/** `MTG-YYYY-###` — three random digits (low collision risk for the
 *  small board-meeting volume; not a true ID, just a human-friendly
 *  reference number that the user can override in the form). */
export function genMeetingNumber(): string {
  return `MTG-${dayjs().format('YYYY')}-${String(Math.floor(100 + Math.random() * 900))}`;
}

/** `RES-YYYY-###` — same pattern as genMeetingNumber for resolutions. */
export function genVoteNumber(): string {
  return `RES-${dayjs().format('YYYY')}-${String(Math.floor(100 + Math.random() * 900))}`;
}

export const BOARD_ROLE_OPTIONS = [
  'President', 'Vice President', 'Secretary', 'Treasurer', 'Director', 'Member at Large',
] as const;

export const RESOLUTION_CATEGORY_OPTIONS = [
  'Resolution', 'Budget Approval', 'Bylaw Amendment',
  'Capital Expenditure', 'Election', 'Special Assessment', 'Other',
] as const;
